/**
 * MeshNetworkPanel - Admin panel for AstraeaMesh management
 * 
 * Provides UI for:
 * - Network overview and statistics
 * - Node management (view, revoke)
 * - Auth key management (create, revoke)
 * - Site/office management
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Computer as ComputerIcon,
  Cloud as CloudIcon,
  Key as KeyIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { meshService } from '../../services/meshService';

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Status chip component
function StatusChip({ status }) {
  const color = status === 'online' ? 'success' : status === 'offline' ? 'default' : 'warning';
  return <Chip label={status} color={color} size="small" />;
}

// Overview tab content
function OverviewTab({ overview, loading, onRefresh }) {
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  
  if (!overview) {
    return <Alert severity="info">Unable to load network overview</Alert>;
  }
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Total Nodes</Typography>
            <Typography variant="h3">{overview.total_nodes || 0}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Online</Typography>
            <Typography variant="h3" color="success.main">{overview.online_nodes || 0}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Peer Connections</Typography>
            <Typography variant="h3">{overview.total_peer_connections || 0}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Mesh Subnet</Typography>
            <Typography variant="h6">{overview.mesh_subnet || 'N/A'}</Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// Nodes tab content
function NodesTab({ nodes, loading, onRefresh, onRevokeNode }) {
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Mesh IP</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Tags</TableCell>
            <TableCell>Routes</TableCell>
            <TableCell>Last Seen</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {nodes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center">No nodes registered</TableCell>
            </TableRow>
          ) : (
            nodes.map((node) => (
              <TableRow key={node.fingerprint}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ComputerIcon color="action" />
                    {node.name}
                  </Box>
                </TableCell>
                <TableCell><code>{node.mesh_ip}</code></TableCell>
                <TableCell><StatusChip status={node.status} /></TableCell>
                <TableCell>
                  {node.tags?.map(tag => (
                    <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5 }} />
                  ))}
                </TableCell>
                <TableCell>
                  {node.advertised_routes?.map(route => (
                    <Chip key={route} label={route} size="small" color="info" sx={{ mr: 0.5 }} />
                  )) || '-'}
                </TableCell>
                <TableCell>{node.last_seen ? new Date(node.last_seen).toLocaleString() : '-'}</TableCell>
                <TableCell>
                  <Tooltip title="Revoke node">
                    <IconButton size="small" color="error" onClick={() => onRevokeNode(node)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Auth Keys tab content
function AuthKeysTab({ authKeys, loading, onRefresh, onCreate, onRevoke }) {
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  
  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate}>
          Create Auth Key
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Uses</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {authKeys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">No auth keys created</TableCell>
              </TableRow>
            ) : (
              authKeys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell><code>{key.id}</code></TableCell>
                  <TableCell>{key.description || '-'}</TableCell>
                  <TableCell><Chip label={key.type} size="small" /></TableCell>
                  <TableCell>
                    {key.tags?.map(tag => (
                      <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>{key.max_uses > 0 ? `${key.use_count}/${key.max_uses}` : key.use_count}</TableCell>
                  <TableCell>
                    <StatusChip status={key.revoked ? 'revoked' : 'active'} />
                  </TableCell>
                  <TableCell>{new Date(key.expires_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Tooltip title="Revoke key">
                      <IconButton size="small" color="error" onClick={() => onRevoke(key)} disabled={key.revoked}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

// Create Auth Key Dialog
function CreateAuthKeyDialog({ open, onClose, onCreate }) {
  const [formData, setFormData] = useState({
    organization_id: 'astraea-law',
    description: '',
    key_type: 'reusable',
    tags: '',
    expiry_days: 90
  });
  const [loading, setLoading] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  
  const handleCreate = async () => {
    setLoading(true);
    try {
      const result = await onCreate({
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      });
      setCreatedKey(result.key);
    } catch (error) {
      console.error('Failed to create auth key:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopy = () => {
    navigator.clipboard.writeText(createdKey);
  };
  
  const handleClose = () => {
    setCreatedKey(null);
    setFormData({
      organization_id: 'astraea-law',
      description: '',
      key_type: 'reusable',
      tags: '',
      expiry_days: 90
    });
    onClose();
  };
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyIcon /> Create Auth Key
        </Box>
      </DialogTitle>
      <DialogContent>
        {createdKey ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Auth key created successfully! Copy it now - it won't be shown again.
            </Alert>
            <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <code style={{ flex: 1, wordBreak: 'break-all' }}>{createdKey}</code>
                <IconButton onClick={handleCopy}>
                  <CopyIcon />
                </IconButton>
              </Box>
            </Paper>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Use this key to enroll nodes:
              <br />
              <code>meshd enroll --server URL --auth-key {createdKey}</code>
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Server deployment key"
            />
            <FormControl fullWidth>
              <InputLabel>Key Type</InputLabel>
              <Select
                value={formData.key_type}
                label="Key Type"
                onChange={(e) => setFormData({ ...formData, key_type: e.target.value })}
              >
                <MenuItem value="reusable">Reusable (for servers)</MenuItem>
                <MenuItem value="single-use">Single-use (one device)</MenuItem>
                <MenuItem value="preauthorized">Preauthorized (auto-approve)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Tags"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="firm-node, server"
              helperText="Comma-separated tags to assign to enrolled nodes"
            />
            <TextField
              label="Expires in (days)"
              type="number"
              value={formData.expiry_days}
              onChange={(e) => setFormData({ ...formData, expiry_days: parseInt(e.target.value) })}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{createdKey ? 'Close' : 'Cancel'}</Button>
        {!createdKey && (
          <Button variant="contained" onClick={handleCreate} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// Main component
export default function MeshNetworkPanel() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [overview, setOverview] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [authKeys, setAuthKeys] = useState([]);
  const [sites, setSites] = useState([]);
  
  const [createKeyDialogOpen, setCreateKeyDialogOpen] = useState(false);
  
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewData, nodesData, keysData, sitesData] = await Promise.all([
        meshService.getNetworkOverview().catch(() => null),
        meshService.getNodes().catch(() => ({ nodes: [] })),
        meshService.getAuthKeys().catch(() => []),
        meshService.getSites().catch(() => [])
      ]);
      
      setOverview(overviewData);
      setNodes(nodesData?.nodes || []);
      setAuthKeys(keysData || []);
      setSites(sitesData || []);
    } catch (err) {
      setError('Failed to load mesh data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const handleRevokeNode = async (node) => {
    if (window.confirm(`Are you sure you want to revoke node "${node.name}"?`)) {
      try {
        await meshService.revokeNode(node.fingerprint);
        loadData();
      } catch (err) {
        console.error('Failed to revoke node:', err);
      }
    }
  };
  
  const handleCreateAuthKey = async (keyData) => {
    const result = await meshService.createAuthKey(keyData);
    loadData();
    return result;
  };
  
  const handleRevokeAuthKey = async (key) => {
    if (window.confirm(`Are you sure you want to revoke auth key "${key.id}"?`)) {
      try {
        await meshService.revokeAuthKey(key.id);
        loadData();
      } catch (err) {
        console.error('Failed to revoke auth key:', err);
      }
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudIcon /> AstraeaMesh Network
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
          Refresh
        </Button>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
        <Tab icon={<CloudIcon />} label="Overview" />
        <Tab icon={<ComputerIcon />} label="Nodes" />
        <Tab icon={<KeyIcon />} label="Auth Keys" />
        <Tab icon={<BusinessIcon />} label="Sites" />
      </Tabs>
      
      <TabPanel value={tabValue} index={0}>
        <OverviewTab overview={overview} loading={loading} onRefresh={loadData} />
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        <NodesTab 
          nodes={nodes} 
          loading={loading} 
          onRefresh={loadData}
          onRevokeNode={handleRevokeNode}
        />
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        <AuthKeysTab 
          authKeys={authKeys} 
          loading={loading} 
          onRefresh={loadData}
          onCreate={() => setCreateKeyDialogOpen(true)}
          onRevoke={handleRevokeAuthKey}
        />
      </TabPanel>
      
      <TabPanel value={tabValue} index={3}>
        <Typography color="textSecondary">Sites management coming soon...</Typography>
      </TabPanel>
      
      <CreateAuthKeyDialog 
        open={createKeyDialogOpen}
        onClose={() => setCreateKeyDialogOpen(false)}
        onCreate={handleCreateAuthKey}
      />
    </Box>
  );
}
